import { attr, controller, target, targets } from "@github/catalyst"

// ⌘K / Ctrl+K command palette. The markup (dialog, input, page links, empty
// state, footer hints) is rendered by CommandPaletteComponent; this element
// opens/closes the <dialog>, filters the page links by their data-label,
// fetches ticket matches from `data-search-url` (server-rendered by
// SearchResultsComponent, swapped into the results area) and handles
// arrow/Enter navigation across both groups. The document-level shortcut
// listener is bound in connectedCallback and removed in disconnectedCallback
// because Turbo Drive keeps the page alive across navigations.
@controller
class CommandPaletteElement extends HTMLElement {
  @target dialog!: HTMLDialogElement
  @target input!: HTMLInputElement
  @target group!: HTMLElement
  @target results!: HTMLElement
  @target empty!: HTMLElement
  @targets items!: HTMLAnchorElement[]
  @attr searchUrl = ""

  private selectedIndex = 0
  private onKeydown = (event: KeyboardEvent) => this.shortcut(event)
  private searchTimer: number | undefined
  private searchRequest: AbortController | undefined

  connectedCallback() {
    document.addEventListener("keydown", this.onKeydown)
    // A snapshot restored from Turbo's cache may have been captured with the
    // palette open; always start closed and unfiltered.
    this.dialog.close()
    this.reset()
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown)
    this.cancelSearch()
  }

  open() {
    if (this.dialog.open) return
    this.reset()
    this.dialog.showModal()
    this.input.focus()
  }

  close() {
    this.dialog.close()
  }

  // Clicking the translucent backdrop (the dialog element itself, outside its
  // children) closes the palette.
  backdropClick(event: MouseEvent) {
    if (event.target === this.dialog) this.close()
  }

  reset() {
    this.input.value = ""
    this.cancelSearch()
    this.results.replaceChildren()
    this.filter()
  }

  filter() {
    const query = this.input.value.trim().toLowerCase()
    let visible = 0
    for (const item of this.pageItems()) {
      const label = (item.getAttribute("data-label") || "").toLowerCase()
      const match = query === "" || label.includes(query)
      item.parentElement!.hidden = !match
      if (match) visible++
    }
    this.group.hidden = visible === 0
    this.updateEmpty()
    this.select(0)
    this.scheduleSearch(this.input.value.trim())
  }

  // Debounced fetch of ticket matches; a newer query aborts the older request
  // so results never arrive out of order.
  private scheduleSearch(query: string) {
    this.cancelSearch()
    if (query.length < 2 || !this.searchUrl) {
      this.results.replaceChildren()
      this.updateEmpty()
      return
    }
    this.searchTimer = window.setTimeout(() => this.search(query), 150)
  }

  private async search(query: string) {
    const request = new AbortController()
    this.searchRequest = request
    try {
      const url = new URL(this.searchUrl, window.location.href)
      url.searchParams.set("q", query)
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: request.signal })
      if (!response.ok || request.signal.aborted) return
      // The body is HTML rendered by SearchResultsComponent on the server.
      this.results.innerHTML = await response.text()
      this.updateEmpty()
      this.select(0)
    } catch (error) {
      if ((error as Error).name !== "AbortError") throw error
    }
  }

  private cancelSearch() {
    window.clearTimeout(this.searchTimer)
    this.searchRequest?.abort()
    this.searchRequest = undefined
  }

  private pageItems(): HTMLAnchorElement[] {
    return this.items.filter((item) => this.group.contains(item))
  }

  private updateEmpty() {
    this.empty.hidden = !this.group.hidden || this.results.childElementCount > 0
  }

  keydown(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.select(this.selectedIndex + 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.select(this.selectedIndex - 1)
        break
      case "Enter": {
        event.preventDefault()
        const item = this.visibleItems()[this.selectedIndex]
        // Turbo Drive intercepts the synthetic click and navigates.
        if (item) item.click()
        break
      }
    }
  }

  hover(event: MouseEvent) {
    const index = this.visibleItems().indexOf(event.currentTarget as HTMLAnchorElement)
    if (index >= 0) this.select(index)
  }

  private shortcut(event: KeyboardEvent) {
    if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return
    event.preventDefault()
    this.dialog.open ? this.close() : this.open()
  }

  private visibleItems(): HTMLAnchorElement[] {
    return this.items.filter((item) => !item.parentElement!.hidden)
  }

  private select(index: number) {
    const visible = this.visibleItems()
    if (visible.length === 0) {
      this.selectedIndex = 0
      return
    }
    this.selectedIndex = (index + visible.length) % visible.length
    for (const item of this.items) item.removeAttribute("aria-selected")
    const selected = visible[this.selectedIndex]
    selected.setAttribute("aria-selected", "true")
    selected.scrollIntoView({ block: "nearest" })
  }
}

export default CommandPaletteElement
